// src/ConfirmByPinPage.tsx
import Wordmark from "./components/Wordmark";
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';

interface EventDoc {
  pin: string;
  gameType?: string;
  datetime?: string;
  venue?: string;
  players?: string[];
  confirmedNames?: string[];
}

const C = {
  bg: '#061a09', card: '#0d2610', border: '#1a4020',
  green: '#16a34a', greenLt: '#4ade80', gold: '#f59e0b',
  white: '#fff', muted: '#6b9e7a',
};

const ConfirmByPinPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('event');

  const [step, setStep]               = useState<'pin'|'name'|'done'>('pin');
  const [pinInput, setPinInput]       = useState('');
  const [eventData, setEventData]     = useState<EventDoc | null>(null);
  const [confirmedNames, setConfirmedNames] = useState<string[]>([]);
  const [selectedName, setSelectedName] = useState('');
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);

  // Lataa tapahtuma URL:n perusteella
  useEffect(() => {
    if (!eventId) return;
    getDoc(doc(db, 'events', eventId)).then(snap => {
      if (snap.exists()) {
        const d = snap.data() as EventDoc;
        setEventData(d);
        setConfirmedNames(d.confirmedNames || []);
      } else {
        setError('Tapahtumaa ei löydy. Tarkista linkki.');
      }
    });
  }, [eventId]);

  // Tarkista PIN
  const handlePin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventData || !pinInput) return;
    if (pinInput.trim() === eventData.pin) {
      setStep('name');
      setError('');
    } else {
      setError('Väärä PIN-koodi. Tarkista sähköpostistasi.');
      setPinInput('');
    }
  };

  // Vahvista nimi
  const handleConfirm = async (name: string) => {
    if (!eventId || loading) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'events', eventId), {
        confirmedNames: arrayUnion(name),
      });
      setConfirmedNames(prev => [...prev, name]);
      setSelectedName(name);
      // Tallenna myös localStorageen → pääsee suoraan sovellukseen
      localStorage.setItem('spinloop_pin', pinInput.trim());
      localStorage.setItem('spinloop_role', 'player');
      localStorage.setItem('spinloop_event', eventId);
      setStep('done');
    } catch {
      setError('Virhe tallennuksessa, yritä uudelleen.');
    }
    setLoading(false);
  };

  const dateStr = eventData?.datetime
    ? new Date(eventData.datetime).toLocaleString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, rgba(245,247,250,0.88) 0%, rgba(238,242,247,0.85) 100%), url('/court-bg.jpg') center/cover`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Sora','Arial',sans-serif", padding: 20,
    }}>
      <img src="/logo192.png" alt="Spinloop" style={{ width: 80, borderRadius: 14, marginBottom: 14 }} />
      <div style={{ fontSize: 24, fontWeight: 900, color: C.white, marginBottom: 4 }}>
        <Wordmark />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 24 }}>Ilmoittautuminen</div>

      {/* Tapahtuma-info */}
      {eventData && (
        <div style={{
          background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '10px 18px', marginBottom: 20,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.white }}>
            {eventData.gameType || 'Pickleball'} Americano
          </div>
          {dateStr && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>📅 {dateStr}</div>}
          {eventData.venue && <div style={{ fontSize: 12, color: C.gold, marginTop: 2 }}>📍 {eventData.venue}</div>}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            {confirmedNames.length} / {(eventData.players || []).length} ilmoittautunut
          </div>
        </div>
      )}

      {/* VAIHE 1: PIN */}
      {step === 'pin' && (
        <form onSubmit={handlePin} style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 16, padding: 24, width: '100%', maxWidth: 320,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.white, textAlign: 'center', marginBottom: 4 }}>
            🔑 Syötä PIN-koodi sähköpostistasi
          </div>
          <input
            type="number" inputMode="numeric"
            value={pinInput}
            onChange={e => setPinInput(e.target.value)}
            placeholder="1234" maxLength={4} autoFocus required
            style={{
              background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
              color: C.white, fontSize: 32, fontWeight: 900,
              textAlign: 'center', padding: '14px 10px', letterSpacing: 10,
              outline: 'none', width: '100%', boxSizing: 'border-box',
            }}
          />
          {error && <div style={{ color: '#f87171', fontSize: 12, textAlign: 'center' }}>{error}</div>}
          <button type="submit" disabled={pinInput.length < 4} style={{
            background: pinInput.length >= 4 ? 'linear-gradient(135deg, #163b68, #2a5c8f)' : C.border,
            color: pinInput.length >= 4 ? C.white : C.muted,
            border: 'none', borderRadius: 10, padding: 14,
            fontSize: 14, fontWeight: 700, cursor: pinInput.length >= 4 ? 'pointer' : 'default',
          }}>
            Jatka →
          </button>
        </form>
      )}

      {/* VAIHE 2: Valitse nimi */}
      {step === 'name' && eventData && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 16, padding: 24, width: '100%', maxWidth: 360,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.white, textAlign: 'center', marginBottom: 16 }}>
            👤 Kuka olet?
          </div>
          {(eventData.players || []).map(name => {
            const already = confirmedNames.includes(name);
            return (
              <button
                key={name}
                onClick={() => !already && handleConfirm(name)}
                disabled={already || loading}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '12px 16px', marginBottom: 8,
                  background: already ? 'rgba(74,222,128,0.1)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${already ? C.greenLt : C.border}`,
                  borderRadius: 12, cursor: already ? 'default' : 'pointer',
                  color: C.white,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: already
                      ? 'linear-gradient(135deg, #163b68, #2a5c8f)'
                      : `linear-gradient(135deg, #2d5a3d, #1a3a2a)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 900, color: C.white, flexShrink: 0,
                  }}>
                    {name[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{name}</span>
                </div>
                {already
                  ? <span style={{ fontSize: 12, color: C.greenLt }}>✓ Ilmoittautunut</span>
                  : <span style={{ fontSize: 12, color: C.muted }}>Valitse →</span>
                }
              </button>
            );
          })}
          {error && <div style={{ color: '#f87171', fontSize: 12, textAlign: 'center', marginTop: 8 }}>{error}</div>}
        </div>
      )}

      {/* VAIHE 3: Vahvistettu */}
      {step === 'done' && (
        <div style={{
          background: 'rgba(22,59,104,0.15)', border: `1px solid rgba(22,59,104,0.4)`,
          borderRadius: 16, padding: 28, textAlign: 'center', maxWidth: 320, width: '100%',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: C.white, marginBottom: 8 }}>
            Olet mukana, {selectedName}!
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
            Ilmoittautumisesi on tallennettu.
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              background: `linear-gradient(135deg, ${C.gold}, #d97706)`,
              color: '#1a0f00', border: 'none', borderRadius: 10,
              padding: '13px 28px', fontSize: 14, fontWeight: 900, cursor: 'pointer',
            }}
          >
            Avaa Spinloop →
          </button>
        </div>
      )}

      {!step.startsWith('done') && (
        <button onClick={() => navigate('/')} style={{
          marginTop: 20, background: 'transparent', color: C.muted,
          border: `1px solid ${C.border}`, borderRadius: 8,
          padding: '8px 18px', fontSize: 12, cursor: 'pointer',
        }}>
          Palaa etusivulle
        </button>
      )}
    </div>
  );
};

export default ConfirmByPinPage;
